import { useCallback, useState } from 'react';
import type { Article, ArticleDraft } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync, useDebounced, useSelection } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import {
  BulkBar,
  Checkbox,
  ConfirmModal,
  Drawer,
  EmptyState,
  ErrorState,
  FilterPills,
  Modal,
  Pager,
  RefreshButton,
  SearchInput,
  StatusBadge,
  TableSkeleton,
  Tag,
  TextAreaField,
  TextField,
} from '../components/ui';
import { PhotoGalleryUpload } from '../components/photo-gallery-upload';
import { PAGE_SIZE } from '../theme/tokens';

type StatusFilter = 'all' | 'draft' | 'published';

const FILTERS = [
  { value: 'all' as const, label: 'Tous' },
  { value: 'draft' as const, label: 'Brouillons' },
  { value: 'published' as const, label: 'Publiés' },
];

const NEW_DRAFT: ArticleDraft = {
  title: '',
  body: '',
  categoryId: null,
  publishNow: false,
  imageKeys: [],
};

export function ArticlesPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vArticles = useRealtimeVersion('articles');
  const vArticleCategories = useRealtimeVersion('article-categories');

  const categoriesResource = useAsync(() => container.articleCategories.list.execute(), [container, vArticleCategories]);
  const categoryOptions = categoriesResource.data ?? [];

  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  const [detail, setDetail] = useState<Article | null>(null);
  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<ArticleDraft>(NEW_DRAFT);
  const [editorArticle, setEditorArticle] = useState<Article | null>(null);
  const [saving, setSaving] = useState(false);

  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const isMobile = useIsMobile();
  const selection = useSelection();

  const resource = useAsync(
    () =>
      container.articles.search.execute({
        status,
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
      }),
    [container, status, debouncedSearch, page, vArticles],
  );

  const rows = resource.data?.items ?? [];
  const visibleIds = rows.map((row) => row.id);
  const editorOpen = editorId !== undefined;

  const setStatusFor = useCallback(
    async (ids: readonly string[], next: 'published' | 'draft') => {
      try {
        await container.articles.setStatus.execute({ ids, status: next });
        toast.notify(
          next === 'published'
            ? `${ids.length} article(s) publié(s)`
            : `${ids.length} article(s) repassé(s) en brouillon`,
        );
        selection.clear();
        resource.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, selection, resource],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await container.articles.remove.execute(id);
        toast.notify('Article supprimé');
        resource.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, resource],
  );

  const openDetail = useCallback((article: Article) => {
    setDetail(article);
  }, []);

  const openEditor = useCallback((article: Article | null) => {
    setDetail(null);
    if (article === null) {
      setEditorId(null);
      setEditorArticle(null);
      setDraft({ ...NEW_DRAFT, categoryId: categoryOptions[0]?.id ?? null });
      return;
    }
    setEditorId(article.id);
    setEditorArticle(article);
    setDraft({
      title: article.title,
      body: article.body,
      categoryId: article.categoryId,
      publishNow: false,
      imageKeys: article.imageKeys,
    });
  }, [categoryOptions]);

  const openDuplicate = useCallback((article: Article) => {
    setDetail(null);
    setEditorId(null);
    setEditorArticle(article);
    setDraft({
      title: `Copie de ${article.title}`,
      body: article.body,
      categoryId: article.categoryId,
      publishNow: false,
      imageKeys: article.imageKeys,
    });
  }, []);

  const save = useCallback(async () => {
    if (editorId === undefined) return;
    setSaving(true);
    try {
      await container.articles.save.execute({ id: editorId, draft });
      toast.notify('Article enregistré');
      setEditorId(undefined);
      setEditorArticle(null);
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editorId, draft, toast, resource]);

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <FilterPills
            options={FILTERS}
            value={status}
            onChange={(next) => {
              setStatus(next);
              setPage(1);
            }}
          />
          <SearchInput
            value={search}
            width={220}
            placeholder="Rechercher un article…"
            onChange={(next) => {
              setSearch(next);
              setPage(1);
            }}
          />
        </div>
        <div className="bbm-toolbar__end">
          <button
            type="button"
            className="bbm-btn bbm-btn--primary"
            disabled={!can('articles', 'create')}
            onClick={() => openEditor(null)}
          >
            + Nouvel article
          </button>
          <RefreshButton onRefresh={resource.reload} loading={resource.loading} />
        </div>
      </div>

      {resource.loading ? <TableSkeleton /> : null}

      {resource.error ? (
        <ErrorState
          status={resource.error.status}
          message={resource.error.message}
          onRetry={resource.reload}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length === 0 ? (
        <EmptyState
          title="Aucun résultat"
          body="Aucun article ne correspond à ces filtres."
          actionLabel="Réinitialiser les filtres"
          onAction={() => {
            setStatus('all');
            setSearch('');
            setPage(1);
          }}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length > 0 && resource.data ? (
        <>
          <BulkBar count={selection.selected.length} onClear={selection.clear}>
            <button
              type="button"
              className="bbm-btn bbm-btn--success"
              onClick={() => setPending({
                title: 'Publier la sélection ?',
                body: 'Les articles sélectionnés seront visibles dans l\'application.',
                confirmLabel: 'Publier',
                fn: () => void setStatusFor(selection.selected, 'published'),
              })}
            >
              Publier la sélection
            </button>
            <button
              type="button"
              className="bbm-bulkbar__ghost"
              onClick={() => setPending({
                title: 'Dépublier la sélection ?',
                body: 'Les articles sélectionnés ne seront plus visibles dans l\'application.',
                confirmLabel: 'Dépublier',
                danger: true,
                fn: () => void setStatusFor(selection.selected, 'draft'),
              })}
            >
              Repasser en brouillon
            </button>
          </BulkBar>

          {isMobile ? (
            <div className="bbm-card-stack">
              {rows.map((article) => (
                <div key={article.id} className="bbm-mobile-card">
                  <div className="bbm-mobile-card__head">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1, minWidth: 0 }}>
                      <Checkbox
                        checked={selection.isSelected(article.id)}
                        onChange={() => selection.toggle(article.id)}
                        label={`Sélectionner ${article.title}`}
                      />
                      {article.coverUrl ? (
                        <img src={article.coverUrl} alt="" aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 8, background: '#EEF1F4', flexShrink: 0 }} />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div className="bbm-mobile-card__title">{article.title}</div>
                        <div className="bbm-mobile-card__sub">{article.categoryLabel ?? '—'}</div>
                      </div>
                    </div>
                    <StatusBadge status={article.publishedAt ? 'published' : 'draft'} />
                  </div>
                  <div className="bbm-mobile-card__fields">
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Date</span>
                      <span>{article.publishedAt ? article.publishedAt.slice(0, 10) : '—'}</span>
                    </div>
                  </div>
                  <div className="bbm-mobile-card__actions">
                    <button type="button" className="bbm-btn" onClick={() => openDetail(article)}>Voir</button>
                    <button type="button" className="bbm-btn" onClick={() => {
                      if (article.publishedAt) {
                        setPending({ title: 'Dépublier cet article ?', body: "Il ne sera plus visible dans l'application.", confirmLabel: 'Dépublier', danger: true, fn: () => void setStatusFor([article.id], 'draft') });
                      } else {
                        setPending({ title: 'Publier cet article ?', body: "Il sera visible dans l'application.", confirmLabel: 'Publier', fn: () => void setStatusFor([article.id], 'published') });
                      }
                    }}>{article.publishedAt ? 'Dépublier' : 'Publier'}</button>
                    <button type="button" className="bbm-btn" disabled={!can('articles', 'edit')} onClick={() => openEditor(article)}>Modifier</button>
                    <button type="button" className="bbm-btn" disabled={!can('articles', 'create')} onClick={() => openDuplicate(article)}>Dupliquer</button>
                    <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('articles', 'delete')} title={can('articles', 'delete') ? '' : 'Réservé au super-admin'} onClick={() => setPending({
                      title: 'Supprimer cet article ?', body: 'Cette action est irréversible.', confirmLabel: 'Supprimer', danger: true, fn: () => void remove(article.id),
                    })}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bbm-panel">
              <div className="bbm-row bbm-row--head">
                <Checkbox
                  checked={selection.allSelected(visibleIds)}
                  onChange={() => selection.toggleAll(visibleIds)}
                  label="Tout sélectionner"
                />
                <div style={{ width: 44 }}>Cover</div>
                <div style={{ width: 230 }}>Titre</div>
                <div style={{ width: 130 }}>Catégorie</div>
                <div style={{ width: 100 }}>Statut</div>
                <div style={{ width: 100 }}>Date</div>
                <div style={{ marginLeft: 'auto' }}>Actions</div>
              </div>

              {rows.map((article) => (
                <div key={article.id} className="bbm-row">
                  <Checkbox
                    checked={selection.isSelected(article.id)}
                    onChange={() => selection.toggle(article.id)}
                    label={`Sélectionner ${article.title}`}
                  />
                  <div
                    aria-hidden="true"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 8,
                      background: article.coverUrl ? `url(${article.coverUrl}) center/cover` : '#EEF1F4',
                      flexShrink: 0,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => openDetail(article)}
                    style={{ width: 230, textAlign: 'left', border: 'none', background: 'none', padding: 0 }}
                  >
                    <div className="bbm-cell-strong bbm-truncate">{article.title}</div>
                    {article.excerpt ? (
                      <div className="bbm-cell-sub bbm-truncate">{article.excerpt}</div>
                    ) : null}
                  </button>
                  <div style={{ width: 130, flexShrink: 0, overflow: 'hidden' }}>
                    <span
                      className="bbm-badge"
                      title={article.categoryLabel ?? undefined}
                      style={{
                        background: '#7FB3D522',
                        color: '#33506B',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {article.categoryLabel ?? '—'}
                    </span>
                  </div>
                  <div style={{ width: 100 }}>
                    <StatusBadge status={article.publishedAt ? 'published' : 'draft'} />
                  </div>
                  <div className="bbm-cell" style={{ width: 100, color: '#8A95A3' }}>
                    {article.publishedAt ? article.publishedAt.slice(0, 10) : '—'}
                  </div>
                  <div className="bbm-row__actions">
                    <button type="button" className="bbm-btn" onClick={() => openDetail(article)}>
                      Voir
                    </button>
                    <button
                      type="button"
                      className="bbm-btn"
                      onClick={() => {
                        if (article.publishedAt) {
                          setPending({
                            title: 'Dépublier cet article ?',
                            body: 'Il ne sera plus visible dans l\'application.',
                            confirmLabel: 'Dépublier',
                            danger: true,
                            fn: () => void setStatusFor([article.id], 'draft'),
                          });
                        } else {
                          setPending({
                            title: 'Publier cet article ?',
                            body: 'Il sera visible dans l\'application.',
                            confirmLabel: 'Publier',
                            fn: () => void setStatusFor([article.id], 'published'),
                          });
                        }
                      }}
                    >
                      {article.publishedAt ? 'Dépublier' : 'Publier'}
                    </button>
                    <button type="button" className="bbm-btn" disabled={!can('articles', 'edit')} onClick={() => openEditor(article)}>
                      Modifier
                    </button>
                    <button type="button" className="bbm-btn" disabled={!can('articles', 'create')} onClick={() => openDuplicate(article)}>
                      Dupliquer
                    </button>
                    <button
                      type="button"
                      className="bbm-btn bbm-btn--danger"
                      disabled={!can('articles', 'delete')}
                      title={can('articles', 'delete') ? '' : 'Réservé au super-admin'}
                      onClick={() => setPending({
                        title: 'Supprimer cet article ?',
                        body: 'Cette action est irréversible.',
                        confirmLabel: 'Supprimer',
                        danger: true,
                        fn: () => void remove(article.id),
                      })}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Pager
            page={resource.data.page}
            pageCount={resource.data.pageCount}
            total={resource.data.total}
            pageSize={resource.data.pageSize}
            onChange={setPage}
          />
        </>
      ) : null}

      {detail ? (
        <Drawer
          title={detail.title}
          subtitle={detail.categoryLabel ?? undefined}
          onClose={() => setDetail(null)}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
            <StatusBadge status={detail.publishedAt ? 'published' : 'draft'} />
            {detail.isFeatured ? (
              <Tag background="#E0A55222" color="#8A5B22">
                À la une
              </Tag>
            ) : null}
          </div>

          {detail.imageUrls.length > 0 ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              {detail.imageUrls.map((url, i) => (
                <div
                  key={url}
                  style={{
                    width: i === 0 ? 180 : 84,
                    height: i === 0 ? 120 : 64,
                    borderRadius: 10,
                    background: `url(${url}) center/cover`,
                    flexShrink: 0,
                  }}
                />
              ))}
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {detail.excerpt ? (
              <div>
                <div className="bbm-detail-label">Extrait</div>
                <div style={{ fontSize: 13.5, color: '#4A5560', lineHeight: 1.55 }}>
                  {detail.excerpt}
                </div>
              </div>
            ) : null}
            <div>
              <div className="bbm-detail-label">Corps de l'article</div>
              <div
                style={{
                  fontSize: 13,
                  color: '#4A5560',
                  lineHeight: 1.6,
                  maxHeight: 220,
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {detail.body}
              </div>
            </div>
            <div>
              <div className="bbm-detail-label">
                {detail.publishedAt ? 'Publié le' : 'Non publié'}
              </div>
              {detail.publishedAt ? (
                <div className="bbm-detail-value">{detail.publishedAt.slice(0, 10)}</div>
              ) : null}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 26 }}>
            <button
              type="button"
              className="bbm-btn bbm-btn--block"
              onClick={() => {
                if (detail.publishedAt) {
                  setPending({
                    title: 'Dépublier cet article ?',
                    body: 'Il ne sera plus visible dans l\'application.',
                    confirmLabel: 'Dépublier',
                    danger: true,
                    fn: () => void setStatusFor([detail.id], 'draft'),
                  });
                } else {
                  setPending({
                    title: 'Publier cet article ?',
                    body: 'Il sera visible dans l\'application.',
                    confirmLabel: 'Publier',
                    fn: () => void setStatusFor([detail.id], 'published'),
                  });
                }
              }}
            >
              {detail.publishedAt ? 'Dépublier' : 'Publier'}
            </button>
            <button
              type="button"
              className="bbm-btn bbm-btn--block"
              disabled={!can('articles', 'edit')}
              onClick={() => openEditor(detail)}
            >
              Modifier l'article
            </button>
            <button
              type="button"
              className="bbm-btn bbm-btn--block"
              disabled={!can('articles', 'create')}
              onClick={() => openDuplicate(detail)}
            >
              Dupliquer l'article
            </button>
            <button
              type="button"
              className="bbm-btn bbm-btn--danger bbm-btn--block"
              disabled={!can('articles', 'delete')}
              title={can('articles', 'delete') ? '' : 'Réservé au super-admin'}
              onClick={() => setPending({
                title: 'Supprimer cet article ?',
                body: 'Cette action est irréversible.',
                confirmLabel: 'Supprimer',
                danger: true,
                fn: () => { void remove(detail.id); setDetail(null); },
              })}
            >
              Supprimer
            </button>
          </div>
        </Drawer>
      ) : null}

      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {editorOpen ? (
        <Modal
          title={editorId === null ? 'Nouvel article' : "Modifier l'article"}
          onClose={() => { setEditorId(undefined); setEditorArticle(null); }}
          onSubmit={() => void save()}
          busy={saving}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 20 }}>
            <div>
              <TextField
                label="Titre"
                value={draft.title}
                onChange={(value) => setDraft({ ...draft, title: value })}
              />
              <div style={{ marginBottom: 12 }}>
                <label className="bbm-label">Catégorie</label>
                <select
                  className="bbm-input"
                  value={draft.categoryId ?? ''}
                  onChange={(e) => setDraft({ ...draft, categoryId: e.target.value || null })}
                >
                  <option value="">— Sans catégorie —</option>
                  {categoryOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <span className="bbm-label">Photos</span>
              <PhotoGalleryUpload
                key={editorId ?? 'new'}
                initialKeys={draft.imageKeys ?? []}
                initialUrls={editorArticle?.imageUrls ?? []}
                purpose="article-cover"
                onChange={(keys) => setDraft((d) => ({ ...d, imageKeys: keys }))}
                onRequestUpload={container.media.requestUpload}
              />
            </div>
          </div>
          <TextAreaField
            label="Corps de l'article"
            rows={6}
            value={draft.body}
            onChange={(value) => setDraft({ ...draft, body: value })}
          />
        </Modal>
      ) : null}
    </>
  );
}
